import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import liff from "@line/liff";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

type LineProfile = {
  userId: string;
  displayName: string;
  pictureUrl?: string;
};

type Status = "init" | "ready" | "linking" | "linked" | "error";

const LIFF_ID =
  (import.meta.env.VITE_TEACHER_LINE_LINK_LIFF_ID as string | undefined) ||
  "2009974682-gLxR90ry";

export default function TeacherLineLinkPage() {
  const [params] = useSearchParams();

  // teacher_code อาจมาในหลายรูปแบบ:
  // 1) ?teacher_code=FS0001
  // 2) ?liff.state=%3Fteacher_code%3DFS0001  (LIFF redirect)
  // 3) หายไปหลัง LINE login redirect → fallback จาก sessionStorage
  const STORAGE_KEY = "teacher_line_link.teacher_code";

  const resolveTeacherCode = (): string => {
    const direct = params.get("teacher_code")?.trim();
    if (direct) return direct;

    const liffState = params.get("liff.state");
    if (liffState) {
      try {
        const decoded = decodeURIComponent(liffState);
        const qIdx = decoded.indexOf("?");
        const qs = qIdx >= 0 ? decoded.slice(qIdx + 1) : decoded;
        const sp = new URLSearchParams(qs);
        const fromState = sp.get("teacher_code")?.trim();
        if (fromState) return fromState;
      } catch {
        /* ignore */
      }
    }

    if (typeof window !== "undefined") {
      const stored = window.sessionStorage.getItem(STORAGE_KEY)?.trim();
      if (stored) return stored;
    }
    return "";
  };

  const teacherCode = resolveTeacherCode();

  const [status, setStatus] = useState<Status>("init");
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<LineProfile | null>(null);
  const [linkedInfo, setLinkedInfo] = useState<{
    teacher_name?: string;
    teacher_nickname?: string;
  } | null>(null);

  useEffect(() => {
    (async () => {
      try {
        if (!LIFF_ID) {
          throw new Error("ยังไม่ได้ตั้งค่า VITE_TEACHER_LINE_LINK_LIFF_ID");
        }
        if (!teacherCode) {
          throw new Error("กรุณาเปิดลิงก์จากแชท/Rich Menu (ขาดพารามิเตอร์ teacher_code)");
        }
        // เก็บไว้กันหายตอน LINE redirect กลับมา
        try {
          window.sessionStorage.setItem(STORAGE_KEY, teacherCode);
        } catch {
          /* ignore */
        }
        await liff.init({ liffId: LIFF_ID });
        if (!liff.isLoggedIn()) {
          liff.login({ redirectUri: window.location.href });
          return;
        }
        const p = await liff.getProfile();
        setProfile(p);
        setStatus("ready");
      } catch (e) {
        const msg = e instanceof Error ? e.message : "เกิดข้อผิดพลาดในการเริ่มต้น LIFF";
        setError(msg);
        setStatus("error");
      }
    })();
  }, [teacherCode]);

  const handleLink = async () => {
    setStatus("linking");
    setError(null);
    try {
      const idToken = liff.getIDToken();
      if (!idToken) throw new Error("ไม่พบ LINE ID Token");

      const { data, error: invokeErr } = await supabase.functions.invoke(
        "link-teacher-line-user",
        {
          body: {
            teacher_code: teacherCode,
            line_id_token: idToken,
          },
        },
      );

      if (invokeErr) {
        // Try to read JSON error
        let msg = invokeErr.message;
        try {
          const ctx = (invokeErr as unknown as { context?: Response }).context;
          if (ctx && typeof ctx.json === "function") {
            const body = await ctx.json();
            if (body?.error) msg = body.error;
          }
        } catch {
          /* ignore */
        }
        throw new Error(msg);
      }

      setLinkedInfo({
        teacher_name: data?.teacher_name,
        teacher_nickname: data?.teacher_nickname,
      });
      setStatus("linked");
      try {
        window.sessionStorage.removeItem(STORAGE_KEY);
      } catch {
        /* ignore */
      }
      toast.success("ผูกบัญชี LINE สำเร็จ");
    } catch (e) {
      const msg = e instanceof Error ? e.message : "เกิดข้อผิดพลาด";
      setError(msg);
      setStatus("error");
      toast.error(msg);
    }
  };

  return (
    <div className="min-h-screen bg-muted/30 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>ผูกบัญชี LINE กับครู</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {status === "init" && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> กำลังเชื่อมต่อ LINE...
            </div>
          )}

          {status === "error" && (
            <div className="flex items-start gap-2 text-destructive">
              <AlertCircle className="h-5 w-5 mt-0.5" />
              <div className="text-sm">{error}</div>
            </div>
          )}

          {(status === "ready" || status === "linking") && profile && (
            <>
              <div className="flex items-center gap-3">
                {profile.pictureUrl && (
                  <img
                    src={profile.pictureUrl}
                    alt={profile.displayName}
                    className="h-14 w-14 rounded-full"
                  />
                )}
                <div>
                  <div className="font-medium">{profile.displayName}</div>
                  <div className="text-xs text-muted-foreground break-all">
                    {profile.userId}
                  </div>
                </div>
              </div>
              <div className="rounded-md border p-3 text-sm">
                จะผูก LINE นี้กับรหัสครู:{" "}
                <span className="font-semibold">{teacherCode}</span>
              </div>
              <Button
                onClick={handleLink}
                disabled={status === "linking"}
                className="w-full"
              >
                {status === "linking" && (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                )}
                ยืนยันผูกบัญชี LINE
              </Button>
            </>
          )}

          {status === "linked" && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-green-600">
                <CheckCircle2 className="h-5 w-5" />
                <span className="font-medium">ผูกบัญชีสำเร็จ</span>
              </div>
              <div className="rounded-md border p-3 text-sm space-y-1">
                <div>
                  รหัสครู: <span className="font-semibold">{teacherCode}</span>
                </div>
                {linkedInfo?.teacher_name && (
                  <div>
                    ชื่อ:{" "}
                    <span className="font-semibold">
                      {linkedInfo.teacher_name}
                      {linkedInfo.teacher_nickname
                        ? ` (${linkedInfo.teacher_nickname})`
                        : ""}
                    </span>
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                คุณสามารถปิดหน้านี้ได้แล้ว
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}